import React, { useState } from 'react'
import { Container, Button } from 'components'
import {
    SENTRY_SMOKE_TEST_SCENARIOS,
    SENTRY_SMOKE_TEST_TAG,
    SentrySmokeTestScenarioId,
    sendSentrySmokeTestEvent,
    triggerUncaughtExceptionSmokeTest,
    triggerUnhandledRejectionSmokeTest,
    triggerNetworkFailureSmokeTest,
    triggerTimeoutSmokeTest,
    triggerBadResponseSmokeTest
} from 'helpers/sentrySmokeTest'

/**
 * Dev/staging-only page for verifying that Sentry actually receives events
 * for each category of error the app can produce (not just that the SDK is
 * configured). Each scenario exercises the real reporting code path
 * (connectApi's interceptor, the global error handlers, the ErrorBoundary)
 * rather than calling Sentry directly, wherever possible.
 *
 * Not reachable in production - see the route guard in AppConnectRoutes.
 */
export const SentrySmokeTestPage = () => {
    const [selectedScenario, setSelectedScenario] =
        useState<SentrySmokeTestScenarioId>('manual_exception')
    const [lastTestId, setLastTestId] = useState<string | null>(null)
    const [throwRenderError, setThrowRenderError] = useState(false)

    if (throwRenderError) {
        throw new Error('Connect Sentry smoke test - render error')
    }

    const handleSendTestEvent = async () => {
        switch (selectedScenario) {
            case 'uncaught_exception':
                setLastTestId(triggerUncaughtExceptionSmokeTest())
                break
            case 'unhandled_rejection':
                setLastTestId(triggerUnhandledRejectionSmokeTest())
                break
            case 'network_failure':
                setLastTestId(await triggerNetworkFailureSmokeTest())
                break
            case 'timeout':
                setLastTestId(await triggerTimeoutSmokeTest())
                break
            case 'bad_response':
                setLastTestId(await triggerBadResponseSmokeTest())
                break
            case 'render_error':
                setThrowRenderError(true)
                break
            case 'manual_exception':
            default:
                setLastTestId(sendSentrySmokeTestEvent())
        }
    }

    return (
        <Container>
            <h1>Sentry Smoke Test</h1>
            <p>
                Choose an error scenario and confirm it reaches Sentry. The
                manual exception and HTTP scenarios are tagged with{' '}
                <code>{SENTRY_SMOKE_TEST_TAG}:true</code> so they can be found
                (and filtered out of real issues) in the Sentry dashboard.
            </p>
            <fieldset>
                <legend>Scenario</legend>
                {SENTRY_SMOKE_TEST_SCENARIOS.map((scenario) => (
                    <div key={scenario.id} style={{ marginBottom: '12px' }}>
                        <label>
                            <input
                                type="radio"
                                name="sentry-smoke-test-scenario"
                                value={scenario.id}
                                checked={selectedScenario === scenario.id}
                                onChange={() =>
                                    setSelectedScenario(scenario.id)
                                }
                            />{' '}
                            <strong>{scenario.label}</strong>
                        </label>
                        <p style={{ margin: '0 0 0 24px', color: '#666' }}>
                            {scenario.description}
                        </p>
                    </div>
                ))}
            </fieldset>
            <Button onClick={handleSendTestEvent}>Send test event</Button>
            {lastTestId && (
                <p data-testid="sentry-smoke-test-result">
                    Sent test event {lastTestId}. Search Sentry for{' '}
                    <code>{SENTRY_SMOKE_TEST_TAG}:true</code> to confirm it
                    arrived.
                </p>
            )}
        </Container>
    )
}
